package screens.post

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.presentation.postdetail.PostDetailEvent
import com.example.readingnotes.presentation.postdetail.PostDetailViewModel
import com.example.readingnotes.util.DateFormatter
import design.AccessibilityGuidelines
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import utils.SseClient
import utils.sanitizeHtml

@Composable
fun PostDetailScreen(
    postId: String,
    onBackClick: () -> Unit,
    isLoggedIn: Boolean = false,
    onEditClick: (String) -> Unit = {},
    appModule: AppModule,
    appScope: CoroutineScope,
) {
    val viewModel =
        remember {
            PostDetailViewModel(
                loadPostDetailUseCase = appModule.loadPostDetailUseCase,
                loadCommentsUseCase = appModule.loadCommentsUseCase,
                createCommentUseCase = appModule.createCommentUseCase,
                createPurchaseSessionUseCase = appModule.createPurchaseSessionUseCase,
                viewModelScope = appScope,
            )
        }
    val state by viewModel.state.collectAsState()

    // SSE client for real-time comment updates
    val apiBaseUrl = js("window.API_BASE_URL || 'http://localhost:3001'") as String
    val sseClient = remember { SseClient.create(apiBaseUrl) }

    LaunchedEffect(postId) {
        viewModel.onEvent(PostDetailEvent.OnAppear(postId))

        // Set up SSE event listeners for comments
        sseClient.addEventListener("comment.created") { event ->
            console.log("SSE: Comment created event received", event.data)
            // Announce to screen readers
            AccessibilityGuidelines.announce("新しいコメントが追加されました")
            // Reload comments to get the latest list
            viewModel.onEvent(PostDetailEvent.OnLoadComments(postId))
        }

        sseClient.addEventListener("comment.deleted") { event ->
            console.log("SSE: Comment deleted event received", event.data)
            // Reload comments to get the latest list
            viewModel.onEvent(PostDetailEvent.OnLoadComments(postId))
        }

        // Connect to SSE
        sseClient.connect()
    }

    // Cleanup SSE connection on dispose
    DisposableEffect(Unit) {
        onDispose {
            sseClient.disconnect()
        }
    }

    Div({
        classes(AppStyleSheet.detailContainer)
        attr("data-testid", "post-detail")
    }) {
        // Header with back button and edit button
        Div({ classes(AppStyleSheet.detailHeader) }) {
            Button({
                classes(AppStyleSheet.backButton)
                onClick { onBackClick() }
            }) { Text("← 戻る") }

            // Show edit button only for logged-in admins
            if (isLoggedIn) {
                Button({
                    classes(AppStyleSheet.editButton)
                    onClick { onEditClick(postId) }
                }) { Text("編集") }
            }
        }

        when {
            state.isLoading -> {
                Div({
                    classes(AppStyleSheet.loading)
                    attr("role", "status")
                    attr("aria-live", "polite")
                    attr("aria-label", "投稿詳細を読み込み中")
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "center")
                        property("gap", "16px")
                    }
                }) {
                    Div({ classes(AppStyleSheet.loadingSpinner) }) {}
                    Text("読み込み中...")
                }
            }
            state.errorMessage != null -> {
                Div({
                    classes(AppStyleSheet.error)
                    attr("data-testid", "error-page")
                }) {
                    Text(state.errorMessage!!)
                    Button({
                        classes(AppStyleSheet.primaryButton)
                        attr("aria-label", "ページを再読み込み")
                        style {
                            marginTop(16.px)
                        }
                        onClick {
                            // Reload the page to retry
                            kotlinx.browser.window.location.reload()
                        }
                    }) { Text("リロード") }
                }
            }
            state.post != null -> {
                // Safe to use !! because we already checked != null above
                val post = state.post!!
                // Post detail section
                Div({ classes(AppStyleSheet.postDetail) }) {
                    H1({
                        classes(AppStyleSheet.postTitle)
                        attr("data-testid", "post-title")
                    }) { Text(post.title) }
                    Div({ classes(AppStyleSheet.postMeta) }) {
                        Text("投稿日: ${DateFormatter.formatDateTime(post.createdAt)}")
                        val author = post.author
                        if (author != null) {
                            Text(" | 投稿者: ${author.username ?: "匿名"}")
                        }
                    }
                    Div({
                        classes(AppStyleSheet.postBody)
                    }) {
                        // Use safe HTML rendering with DOMPurify to prevent XSS attacks
                        // For paid content without access, show preview
                        val contentHtml = if (!post.hasAccess && post.isPurchasable && post.previewHtml != null) {
                            post.previewHtml!!
                        } else {
                            post.contentHtml ?: sanitizeHtml(post.contentMarkdown.replace("\n", "<br>"))
                        }
                        Div({
                            classes(AppStyleSheet.postBody)
                            attr("innerHTML", contentHtml)
                            attr("data-testid", "post-content")
                        }) { }

                        // Purchase CTA for paid content
                        if (post.isPurchasable && !post.hasAccess) {
                            Div({
                                style {
                                    property("margin-top", "24px")
                                    property("padding", "20px")
                                    property("background-color", Color("#f8f9fa"))
                                    property("border", "1px solid #dee2e6")
                                    property("border-radius", "8px")
                                }
                            }) {
                                H3({
                                    style {
                                        property("margin", "0 0 12px 0")
                                        property("font-size", "18px")
                                    }
                                }) { Text("この記事の続きを見るには購入が必要です") }
                                P({
                                    style {
                                        property("margin", "0 0 16px 0")
                                        property("color", Color("#6c757d"))
                                    }
                                }) {
                                    Text("購入すると全文が閲覧できます")
                                }
                                Button({
                                    classes(AppStyleSheet.primaryButton)
                                    onClick {
                                        viewModel.onEvent(PostDetailEvent.OnPurchaseClick)
                                    }
                                    if (state.isCreatingPurchaseSession) {
                                        attr("disabled", "true")
                                    }
                                }) {
                                    Text(
                                        if (state.isCreatingPurchaseSession) "処理中..."
                                        else "¥${post.price ?: 0} で購入する"
                                    )
                                }
                            }
                        }
                    }
                }

                // Comments section
                Div({ classes(AppStyleSheet.commentsSection) }) {
                    H2 { Text("コメント (${state.comments.size})") }

                    // Comment list
                    if (state.comments.isEmpty()) {
                        P({ classes(AppStyleSheet.noComments) }) { Text("まだコメントはありません") }
                    } else {
                        Div({ classes(AppStyleSheet.commentList) }) {
                            state.comments.forEach { comment ->
                                Div({ classes(AppStyleSheet.commentCard) }) {
                                    Div({ classes(AppStyleSheet.commentHeader) }) {
                                        Span({ classes(AppStyleSheet.commentAuthor) }) { Text(comment.authorName) }
                                        Span({ classes(AppStyleSheet.commentDate) }) {
                                            Text(DateFormatter.formatDateTime(comment.createdAt))
                                        }
                                    }
                                    P({ classes(AppStyleSheet.commentContent) }) { Text(comment.content) }
                                }
                            }
                        }
                    }

                    // Comment form
                    Div({ classes(AppStyleSheet.commentForm) }) {
                        H3 { Text("コメントを投稿") }

                        // Author name input
                        Div({ classes(AppStyleSheet.formGroup) }) {
                            Label { Text("名前（任意）") }
                            Input(type = InputType.Text) {
                                classes(AppStyleSheet.textInput)
                                value(state.authorNameInput)
                                onInput { viewModel.onEvent(PostDetailEvent.OnAuthorNameChanged(it.value)) }
                            }
                        }

                        // Comment content input
                        Div({ classes(AppStyleSheet.formGroup) }) {
                            Label { Text("コメント") }
                            Span({
                                style {
                                    property("color", Color("#dc3545"))
                                    property("margin-left", "4px")
                                }
                                attr("aria-hidden", "true")
                            }) { Text("*") }
                            TextArea(attrs = {
                                classes(AppStyleSheet.textArea)
                                attr("placeholder", "コメントを入力してください...")
                                attr("rows", "4")
                                attr("maxlength", "1000")
                                onInput { viewModel.onEvent(PostDetailEvent.OnCommentContentChanged(it.value)) }
                            }, value = state.commentInput)
                        }

                        // Error message
                        if (state.commentErrorMessage != null) {
                            Div({ classes(AppStyleSheet.formError) }) { Text(state.commentErrorMessage!!) }
                        }

                        // Submit button
                        Button({
                            classes(AppStyleSheet.submitButton)
                            onClick { viewModel.onEvent(PostDetailEvent.OnCommentSubmit) }
                            if (state.isSubmittingComment || state.commentInput.isBlank()) {
                                attr("disabled", "true")
                            }
                            style {
                                if (state.commentInput.isBlank()) {
                                    property("opacity", "0.6")
                                    property("cursor", "not-allowed")
                                }
                            }
                        }) {
                            Text(if (state.isSubmittingComment) "送信中..." else "コメントを送信 (${state.commentInput.length}/1000)")
                        }
                    }
                }
            }
        }

        // Purchase confirmation dialog
        if (state.showPurchaseDialog && state.purchaseCheckoutUrl != null) {
            val checkoutUrl = state.purchaseCheckoutUrl
            Div({
                style {
                    property("position", "fixed")
                    property("top", "0")
                    property("left", "0")
                    property("right", "0")
                    property("bottom", "0")
                    property("background-color", "rgba(0, 0, 0, 0.5)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("z-index", "1000")
                }
            }) {
                Div({
                    style {
                        property("background", "white")
                        property("padding", "24px")
                        property("border-radius", "8px")
                        property("max-width", "400px")
                        property("width", "100%")
                    }
                }) {
                    H2({
                        style {
                            property("margin", "0 0 16px 0")
                            property("font-size", "20px")
                        }
                    }) { Text("外部サイトへ移動します") }
                    P({
                        style {
                            property("margin", "0 0 24px 0")
                            property("line-height", "1.5")
                        }
                    }) {
                        Text("Stripeの安全な決済ページへ移動します。\n購入完了後、このページに戻って記事を閲覧できます。")
                    }
                    Div({
                        style {
                            property("display", "flex")
                            property("gap", "12px")
                            property("justify-content", "flex-end")
                        }
                    }) {
                        Button({
                            style {
                                property("padding", "8px 16px")
                                property("background", "#6c757d")
                                property("color", "white")
                                property("border", "none")
                                property("border-radius", "4px")
                                property("cursor", "pointer")
                            }
                            onClick { viewModel.onEvent(PostDetailEvent.OnDismissPurchaseDialog) }
                        }) { Text("キャンセル") }
                        Button({
                            classes(AppStyleSheet.primaryButton)
                            onClick {
                                // Open checkout URL in new tab
                                checkoutUrl?.let { url ->
                                    kotlinx.browser.window.open(url, "_blank")
                                }
                                viewModel.onEvent(PostDetailEvent.OnDismissPurchaseDialog)
                            }
                        }) { Text("決済ページへ") }
                    }
                }
            }
        }

        // Purchase error dialog
        if (state.purchaseErrorMessage != null) {
            Div({
                style {
                    property("position", "fixed")
                    property("top", "0")
                    property("left", "0")
                    property("right", "0")
                    property("bottom", "0")
                    property("background-color", "rgba(0, 0, 0, 0.5)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("z-index", "1000")
                }
            }) {
                Div({
                    style {
                        property("background", "white")
                        property("padding", "24px")
                        property("border-radius", "8px")
                        property("max-width", "400px")
                        property("width", "100%")
                    }
                }) {
                    H2({
                        style {
                            property("margin", "0 0 16px 0")
                            property("font-size", "20px")
                            property("color", "red")
                        }
                    }) { Text("エラー") }
                    P({
                        style {
                            property("margin", "0 0 24px 0")
                        }
                    }) { Text(state.purchaseErrorMessage!!) }
                    Button({
                        classes(AppStyleSheet.primaryButton)
                        onClick { viewModel.onEvent(PostDetailEvent.OnDismissPurchaseDialog) }
                    }) { Text("OK") }
                }
            }
        }
    }
}
